<template>
  <MainLayout>
    <div class="messages-container">
      <h1>Messages</h1>
      
      <div v-if="loading" class="loading">
        Loading conversations...
      </div>
      
      <div v-else-if="error" class="error">
        {{ error }}
      </div>
      
      <div v-else-if="conversations.length === 0" class="empty-state">
        <div class="empty-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </div>
        <p>No conversations yet</p>
        <router-link to="/" class="btn">Explore characters</router-link>
      </div>
      
      <div v-else class="conversation-list">
        <div 
          v-for="conversation in conversations" 
          :key="conversation.id" 
          class="conversation-item"
          @click="openChat(conversation.id)"
        >
          <div class="avatar">
            <img :src="conversation.character_details.avatar" :alt="conversation.character_details.name">
          </div>
          <div class="conversation-details">
            <div class="conversation-header">
              <h3>{{ conversation.character_details.name }}</h3>
              <span class="time">{{ formatTime(conversation.updated_at) }}</span>
            </div>
            <p class="last-message">
              {{ getLastMessageContent(conversation) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<script>
import MainLayout from '../components/MainLayout.vue'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'Messages',
  components: {
    MainLayout
  },
  computed: {
    ...mapGetters('conversations', ['allConversations', 'loading', 'error']),
    conversations() {
      return this.allConversations
    }
  },
  methods: {
    ...mapActions('conversations', ['fetchConversations']),
    openChat(conversationId) {
      this.$router.push({ name: 'Chat', params: { id: conversationId } })
    },
    formatTime(timestamp) {
      if (!timestamp) return ''
      
      const date = new Date(timestamp)
      const now = new Date()
      
      // If the message is from today, show the time
      if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
      
      // If the message is from this week, show the day name
      const daysDiff = Math.floor((now - date) / (1000 * 60 * 60 * 24))
      if (daysDiff < 7) {
        return date.toLocaleDateString([], { weekday: 'short' })
      }
      
      // Otherwise show the date
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    },
    getLastMessageContent(conversation) {
      if (!conversation.messages || conversation.messages.length === 0) {
        return 'No messages yet'
      }
      
      // Get the last message
      const lastMessage = conversation.messages[conversation.messages.length - 1]
      return lastMessage.content
    }
  },
  created() {
    this.fetchConversations()
  }
}
</script>

<style scoped>
.messages-container {
  padding: 16px;
}

h1 {
  margin-bottom: 20px;
  text-align: center;
}

.loading, .error {
  text-align: center;
  padding: 20px;
  color: var(--tg-theme-hint-color);
}

.error {
  color: #e53935;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.empty-icon {
  margin-bottom: 16px;
  color: var(--tg-theme-hint-color);
}

.empty-state p {
  margin-bottom: 20px;
  color: var(--tg-theme-hint-color);
}

.conversation-list {
  display: flex;
  flex-direction: column;
}

.conversation-item {
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
}

.avatar {
  position: relative;
  width: 50px;
  height: 50px;
  margin-right: 12px;
  flex-shrink: 0;
}

.avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.conversation-details {
  flex: 1;
  min-width: 0; /* Ensures text truncation works */
}

.conversation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.conversation-header h3 {
  margin: 0;
  font-size: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.time {
  font-size: 12px;
  color: var(--tg-theme-hint-color);
  white-space: nowrap;
  margin-left: 8px;
}

.last-message {
  color: var(--tg-theme-hint-color);
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style> 